// @flow
import React from 'react';
import styled from 'styled-components';
import { useFormContext } from 'react-hook-form';
import { colors } from 'bench-style-constants';
import {
  SidebarSection,
  Text,
  checkboxStates,
  Checkbox,
  SelectHookForm,
} from 'bench-internal-theme';
import {
  optimizationOptions,
  thirdPartyLoginOptions,
  volumeOptions,
  // $FlowFixMe - typescript file
} from '../addon-sidebar/formOptions';
import IntegrationSelect from './IntegrationSelect';
import type { LedgerAccount } from '../types';
import { collectionMethodOptions } from './formOptions';
import { FormGroup, CheckboxGroup } from '../FormGroups';

const PBAutoPostText = styled.div`
  color: ${colors.cloud};
  font-size: 12px;
  font-weight: 450;
  line-height: 16px;
  margin-top: 5px;
  margin-left: 28px;
`;

type FunctionalDataCollectionSidebarSectionProps = {
  isSubmitting: boolean,
  setAutoPostEnabled: boolean => void,
  autoPostEnabled: boolean,
  ledgerAccount: ?LedgerAccount,
  isTemporary: boolean,
};

const FunctionalDataCollectionSidebarSection = ({
  isSubmitting,
  setAutoPostEnabled,
  autoPostEnabled,
  ledgerAccount,
  isTemporary,
}: FunctionalDataCollectionSidebarSectionProps) => {
  const { watch } = useFormContext();
  const categoryId = watch('categoryId', undefined);

  return (
    <SidebarSection>
      <FormGroup>
        <Text color={colors.cloud} styleType="small" uppercase>
          Collection Method
        </Text>
        <SelectHookForm
          isDisabled={isSubmitting}
          options={collectionMethodOptions}
          aria-label="Collection Method"
          name="collectionMethod"
          defaultValue={ledgerAccount?.accountSettings?.collectionMethod}
          isSearchable
          rules={{
            required: { value: !isTemporary, message: 'Collection method is required' },
          }}
        />
        <Text color={colors.cloud} styleType="small" uppercase>
          Optimization
        </Text>
        <SelectHookForm
          isDisabled={isSubmitting}
          options={optimizationOptions}
          aria-label="Optimization Status"
          name="optimizationStatus"
          defaultValue={ledgerAccount?.accountSettings?.optimizationStatus}
          isSearchable
          isClearable
        />
        <Text color={colors.cloud} styleType="small" uppercase>
          Integration
        </Text>
        <IntegrationSelect ledgerAccount={ledgerAccount} isSubmitting={isSubmitting} />
        <Text color={colors.cloud} styleType="small" uppercase>
          Third Party Login
        </Text>
        <SelectHookForm
          isDisabled={isSubmitting}
          options={thirdPartyLoginOptions}
          aria-label="Third Party Login"
          name="thirdPartyLoginAvailable"
          defaultValue={ledgerAccount ? ledgerAccount.thirdPartyLoginAvailable : false}
        />
        <Text color={colors.cloud} styleType="small" uppercase>
          Volume
        </Text>
        <SelectHookForm
          isDisabled={isSubmitting}
          options={volumeOptions}
          aria-label="Volume"
          name="volume"
          rules={{ required: { value: true, message: 'Volume is required' } }}
          defaultValue={ledgerAccount?.accountSettings?.volume || 'MEDIUM'}
        />
      </FormGroup>
      <CheckboxGroup>
        <Checkbox
          aria-label="Auto Post Enabled"
          disabled={isSubmitting || categoryId?.value === '9'}
          onClick={() => setAutoPostEnabled(!autoPostEnabled)}
          checkboxState={autoPostEnabled ? checkboxStates.CHECKED : checkboxStates.UNCHECKED}
        />
        <Text color={colors.cloud} styleType="small" uppercase>
          Auto Post Enabled
        </Text>
      </CheckboxGroup>
      {categoryId?.value === '9' && (
        <PBAutoPostText color={colors.cloud} styleType="small">
          Auto post is required for Plaid transactions to post into the P/B feature of the Bench
          app. Customer may upload statements instead to avoid auto post.
        </PBAutoPostText>
      )}
    </SidebarSection>
  );
};

FunctionalDataCollectionSidebarSection.defaultProps = {
  ledgerAccount: undefined,
};

export default FunctionalDataCollectionSidebarSection;
